import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

interface SessionToken {
  token: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private cachedSession: SessionToken | null = null;
  private cachedEncryptionKey: string | null = null;

  constructor(private http: HttpClient) {}

  /**
   * @description Retrieves a valid session token for encrypting OpenAI request parameters.
   * Returns a cached token if still valid (with 5s buffer), otherwise fetches a fresh one from the backend.
   * The token contains a short-lived derived key (60s TTL) used to encrypt params before sending to the backend.
   * @returns {Promise<SessionToken>} A session token object containing `token` string and `expiresAt` timestamp
   * @throws {Error} If the session token fetch from backend fails
   */
  async getSessionToken(): Promise<SessionToken> {
    try {
      // Return cached token if still valid (5s buffer before expiry)
      if (this.cachedSession && Date.now() < this.cachedSession.expiresAt - 5_000) {
        return this.cachedSession;
      }

      this.cachedSession = await firstValueFrom(
        this.http.post<SessionToken>(`${environment.NODE_SERVER}/model/session-token`, {}),
      );

      return this.cachedSession!;
    } catch (error) {
      console.error('Failed to fetch session token:', error);
      this.cachedSession = null; // clear cache so next call retries fresh
      throw new Error('Failed to fetch session token');
    }
  }

  /**
   * @description Clears the cached session token, forcing a fresh fetch on the next `getSessionToken` call.
   * Should be called when a request fails due to token expiry or decryption errors.
   * @returns {void}
   */
  clearSession(): void {
    this.cachedSession = null;
  }

  /**
   * @description Extracts the derived encryption key from a signed session token.
   * Supports both 2-part (payload.signature) and 3-part (header.payload.signature) formats.
   * Uses base64url-safe decoding via jwtDecode to handle standard JWT encoding.
   * @param {string} token - Signed session token in `payload.signature` or `header.payload.signature` format
   * @returns {string} The derived 32-char AES encryption key embedded in the token payload
   */
  extractDerivedKey(token: string): string {
    try {
      if (!token) {
        throw new Error('Token is missing or empty');
      }

      const parts = token.split('.');

      // 2-part: payload.signature (our custom session token)
      // 3-part: header.payload.signature (standard JWT)
      if (parts.length < 2 || parts.length > 3) {
        throw new Error('Invalid token format — expected 2 or 3 parts');
      }

      let decoded: { derivedKey?: string };

      if (parts.length === 3) {
        // standard JWT — use jwtDecode (handles base64url safely)
        decoded = jwtDecode<{ derivedKey?: string }>(token);
      } else {
        // 2-part custom token — decode payload (index 0) with base64url-safe decode
        const payload = parts[0]
          .replace(/-/g, '+') // base64url → base64
          .replace(/_/g, '/'); // base64url → base64

        decoded = JSON.parse(atob(payload));
      }

      if (!decoded?.derivedKey) {
        throw new Error('Derived key not found in token payload');
      }

      return decoded.derivedKey;
    } catch (error) {
      console.error('Failed to extract derived key from token:', error);
      throw new Error('Failed to extract derived key from token');
    }
  }

  /**
   * Fetches the app encryption key from backend once and caches it.
   * Used for general text encryption/decryption across the app.
   *
   * @returns {Promise<string>} The stable AES encryption key
   * @throws {Error} If the key fetch fails
   */
  async getEncryptionKey(): Promise<string> {
    try {
      if (this.cachedEncryptionKey) {
        return this.cachedEncryptionKey; // reuse cached key — no TTL for stable key
      }

      const { token } = await firstValueFrom(
        this.http.post<{ token: string }>(`${environment.NODE_SERVER}/model/encryption-key`, {}),
      );

      // Extract appKey from token payload
      const { appKey } = JSON.parse(atob(token.split('.')[0]));
      this.cachedEncryptionKey = appKey;

      return this.cachedEncryptionKey!;
    } catch (error) {
      console.error('Failed to fetch app encryption key:', error);
      throw new Error('Failed to fetch app encryption key');
    }
  }
}
